/**
 * @fileoverview Centralized config for the assistant-chat file upload pipeline
 * (agent-creation-assistant + ai-task-creation-assistant).
 *
 * Single source of truth shared by:
 *   - FE  apps/frontend/src/modules/Agent/AgentCreation/AgentCreationAssistant/SimpleChatInput.tsx
 *   - BE  apps/backend/src/agent-creation-assistant/agent-creation-assistant.controller.ts
 *   - BE  apps/backend/src/ai-task-creation-assistant/ai-task-creation-assistant.controller.ts
 *   - BE  apps/backend/src/agent-creation-assistant/infrastructure/services/file-extraction.service.ts
 *
 * Why extension-first: browsers do not reliably set `File.type` for many
 * formats (.md, .json, .yaml, code files) because the OS MIME registry does
 * not always carry them. `file.type` ends up `""` or `application/octet-stream`,
 * which silently fails any MIME-only allowlist. We resolve the canonical MIME
 * from the file extension first, then fall back to the browser-provided MIME.
 */

const EXTENSION_TO_MIME: Record<string, string> = {
  // Documents
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // Text / markup
  ".txt": "text/plain",
  ".md": "text/markdown",
  ".markdown": "text/markdown",
  ".csv": "text/csv",
  ".html": "text/html",
  ".htm": "text/html",
  ".json": "application/json",
  ".xml": "application/xml",
  // Spreadsheets
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // Presentations
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Images (extracted via Mistral OCR)
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".webp": "image/webp",
  ".gif": "image/gif"
};

export const ACCEPTED_ASSISTANT_UPLOAD_MIME_TYPES = Array.from(
  new Set(Object.values(EXTENSION_TO_MIME))
);

export const ACCEPTED_ASSISTANT_UPLOAD_EXTENSIONS = Object.keys(EXTENSION_TO_MIME);

/** Comma-separated extension list for the `accept` attribute on `<input type="file">`. */
export const ACCEPTED_ASSISTANT_UPLOAD_ACCEPT_ATTR = ACCEPTED_ASSISTANT_UPLOAD_EXTENSIONS.join(",");

function getExtension(filename: string): string {
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf(".");
  return dot === -1 ? "" : lower.slice(dot);
}

/**
 * Resolve the canonical MIME type for a file, preferring its extension over
 * the (often unreliable) browser-provided MIME. Returns the provided MIME
 * unchanged if no extension match is found.
 */
export function resolveAssistantUploadMimeType(filename: string, providedMimeType: string): string {
  const ext = getExtension(filename);
  return EXTENSION_TO_MIME[ext] ?? providedMimeType;
}

/**
 * True when the (filename, mimeType) pair matches a supported assistant
 * upload type after extension-first normalization.
 */
export function isAcceptedAssistantUpload(filename: string, providedMimeType: string): boolean {
  const resolved = resolveAssistantUploadMimeType(filename, providedMimeType);
  return ACCEPTED_ASSISTANT_UPLOAD_MIME_TYPES.includes(resolved);
}

/** True for any image MIME (after extension-first normalization). */
export function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}

export function isImageFile(filename: string, providedMimeType: string): boolean {
  return isImageMimeType(resolveAssistantUploadMimeType(filename, providedMimeType));
}

/**
 * Accepted assistant upload that is NOT an image — used by the AI task
 * creation multipart filter, where images go through the upload-first path
 * (POST /api/knowledge/uploadChatAttachments) so Claude receives native
 * image content blocks instead of OCR-extracted text.
 */
export function isAcceptedAssistantDocumentUpload(
  filename: string,
  providedMimeType: string
): boolean {
  return (
    isAcceptedAssistantUpload(filename, providedMimeType) &&
    !isImageFile(filename, providedMimeType)
  );
}
